import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Euro, Calendar, CheckCircle, Clock, AlertCircle, CreditCard, Building2, FileText, Download, FileSignature, Gift, ShieldCheck } from "lucide-react";
import ContractStatus from "@/components/contracts/ContractStatus";
import { formatEuro } from "@/lib/contractApi";
import { canCancelAtPeriodEnd, canRevokeCancellation, cancellationChannelLabel, contractDisplayPeriod, documentedAccessLabel, formatContractDate, formatContractPeriod, isWithdrawalVisible, paymentAmountCents, paymentPeriodLabel } from "@/lib/contractUser";
import { openAuthenticatedDocument } from "@/lib/openAuthenticatedDocument";
import { billingEntryTitle, creditReasonLabel, creditStatusLabel, isCreditEntry } from "@/lib/billingHistory";

const UserDashboard = () => {
  const [data, setData] = useState(null);
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);

  // Helper to ensure we always have an array
  const toArray = (d) => Array.isArray(d) ? d : [];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [response, contractResponse] = await Promise.all([
        axios.get(`${API}/user/dashboard`),
        axios.get(`${API}/user/contract`).catch((error) => {
          if (error.response?.status === 404) return null;
          throw error;
        }),
      ]);
      setData(response.data);
      setContract(contractResponse?.data || null);
    } catch (error) {
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "paid":
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case "pending":
        return <Clock className="w-5 h-5 text-yellow-600" />;
      case "overdue":
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "paid":
        return "Bezahlt";
      case "pending":
        return "Ausstehend";
      case "overdue":
        return "Überfällig";
      default:
        return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "paid":
        return "bg-green-50 text-green-700 border-green-200";
      case "pending":
        return "bg-yellow-50 text-yellow-700 border-yellow-200";
      case "overdue":
        return "bg-red-50 text-red-700 border-red-200";
      default:
        return "bg-gray-50 text-gray-700 border-gray-200";
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("de-DE");
  };

  const handleDownloadRechnung = async (zahlungId, event) => {
    event.stopPropagation();
    
    try {
      const response = await axios.get(`${API}/zahlungen/${zahlungId}/rechnung`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Rechnung_${zahlungId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Rechnung wird heruntergeladen");
    } catch (error) {
      // Handle blob response errors properly
      if (error.response && error.response.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const jsonError = JSON.parse(text);
          toast.error(jsonError.detail || "Fehler beim Herunterladen");
        } catch {
          toast.error("Fehler beim Herunterladen");
        }
      } else {
        toast.error(error.response?.data?.detail || "Fehler beim Herunterladen");
      }
    }
  };

  const getIntervallText = (intervall) => {
    switch (intervall) {
      case "monthly":
        return "Monatlich";
      case "quarterly":
        return "Vierteljährlich";
      case "half-yearly":
        return "Halbjährlich";
      case "yearly":
        return "Jährlich";
      default:
        return intervall;
    }
  };

  const handleContractCopy = async () => {
    try {
      await openAuthenticatedDocument(async () => {
        const response = await axios.get(`${API}/user/contracts/${contract.id}/copy`, { responseType: "blob" });
        return response.data;
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || "Vertragskopie konnte nicht geöffnet werden");
    }
  };

  const handleWithdrawal = async () => {
    if (!window.confirm("Widerruf jetzt verbindlich übermitteln? Eine Begründung ist nicht erforderlich.")) return;
    try {
      await axios.post(`${API}/user/contract/withdraw`, { confirmed: true });
      toast.success("Widerruf erfolgreich übermittelt");
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Widerruf konnte nicht übermittelt werden");
    }
  };

  const handleCancellation = async () => {
    if (!window.confirm(`Vertrag verbindlich zum ${formatContractDate(contract.paid_until)} kündigen?`)) return;
    try {
      const response = await axios.post(`${API}/user/contract/cancel`, { confirmed: true });
      setContract((current) => ({ ...current, ...response.data }));
      toast.success("Kündigung zum Ende des bezahlten Zeitraums gespeichert");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kündigung konnte nicht gespeichert werden");
    }
  };

  const handleCancellationRevocation = async () => {
    if (!window.confirm("Kündigung widerrufen und den Vertrag regulär fortführen?")) return;
    try {
      const response = await axios.post(`${API}/user/contract/cancel/revoke`, { confirmed: true });
      setContract((current) => ({ ...current, ...response.data }));
      toast.success("Kündigung widerrufen");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kündigung konnte nicht widerrufen werden");
    }
  };

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-gray-600">Lädt...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in" data-testid="user-dashboard-container">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Meine Zahlungen</h1>
        <p className="text-sm sm:text-base text-gray-600">{data.wache.name} - {data.abteilung.name}</p>
      </div>

      {contract && (
        <Card className="border-red-100 bg-white shadow-sm">
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div><CardTitle className="flex items-center gap-2 text-lg text-gray-900"><FileSignature className="h-5 w-5 text-red-700" />Mein Nutzungsvertrag</CardTitle><p className="mt-1 font-mono text-xs text-gray-500">{contract.contract_number}</p></div>
              <ContractStatus status={contract.status} />
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <ContractValue label="Tarif" value={contract.terms?.tariff_code === "community" ? "Gemeinschaftstarif" : "Einzeltarif"} />
              <ContractValue label="Gesamtpreis je Zeitraum" value={formatEuro(contract.terms?.price_cents)} />
              <ContractValue label={contract.contract_kind === "legacy_migration" && !contract.current_period ? "Zahlungszeitraum Bestand" : "Aktueller Zeitraum"} value={(() => { const period = contractDisplayPeriod(contract); return period ? formatContractPeriod(period.start, period.end) : "–"; })()} />
              <ContractValue label="Nächster Zahlungstermin" value={data.payment_status?.naechste_zahlung_faellig ? formatContractDate(data.payment_status.naechste_zahlung_faellig) : "Noch offen"} />
              <ContractValue label="Dokumentierter Zugangsstatus" value={documentedAccessLabel(contract)} />
            </div>
            <p className="mt-4 text-xs leading-5 text-gray-500">Dieser Zugangsstatus ist eine Information der Verwaltung. Er löst keine automatische technische Sperrung in der Kantinen-App aus.</p>
            <div className="mt-5 flex flex-wrap gap-3 border-t border-gray-100 pt-5">
              <Button variant="outline" onClick={handleContractCopy} className="border-gray-300"><FileText className="mr-2 h-4 w-4" />Vertragskopie öffnen</Button>
              {isWithdrawalVisible(contract) && <Button variant="outline" onClick={handleWithdrawal} className="border-red-300 text-red-700 hover:bg-red-50"><ShieldCheck className="mr-2 h-4 w-4" />Vertrag widerrufen</Button>}
              {canCancelAtPeriodEnd(contract) && <Button variant="outline" onClick={handleCancellation} className="border-slate-300 text-slate-800">Zum {formatContractDate(contract.paid_until)} kündigen</Button>}
              {canRevokeCancellation(contract) && <Button variant="outline" onClick={handleCancellationRevocation} className="border-amber-300 text-amber-800 hover:bg-amber-50">Kündigung widerrufen</Button>}
            </div>
            {contract.status === "cancel_at_period_end" && <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">Die Kündigung ist vorgemerkt. Der Vertrag endet nach dem bezahlten Zeitraum am {formatContractDate(contract.paid_until)}.{contract.cancel_requested_at && <span className="mt-1 block text-xs">Eingegangen am {formatContractDate(contract.cancel_requested_at)} über {cancellationChannelLabel(contract.cancel_channel)}.</span>}</div>}
            {contract.status === "withdrawn" && <div className="mt-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">Der Vertrag wurde widerrufen. Der Zugang ist deaktiviert; eine vorhandene Zahlung wird vollständig zurückerstattet.</div>}
          </CardContent>
        </Card>
      )}

      {/* Payment Status Card */}
      <Card className="card-white card-hover">
        <CardHeader>
          <CardTitle className="text-base sm:text-lg text-gray-900 flex items-center gap-2">
            <Euro className="w-4 h-4 sm:w-5 sm:h-5" />
            Zahlungsstatus
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <p className="text-xs sm:text-sm text-gray-600">Status</p>
                <div className={`mt-1 px-2 sm:px-3 py-1 rounded-full border inline-flex items-center gap-2 text-sm ${getStatusColor(data.payment_status.status)}`}>
                  {getStatusIcon(data.payment_status.status)}
                  <span className="font-semibold">{getStatusText(data.payment_status.status)}</span>
                </div>
              </div>
              <div className="text-left sm:text-right">
                <p className="text-xs sm:text-sm text-gray-600">Betrag</p>
                <p className="text-2xl sm:text-3xl font-bold text-gray-900">{formatEuro(paymentAmountCents(contract, data.abteilung))}</p>
                <p className="text-xs sm:text-sm text-gray-600">{contract ? `${contract.terms?.duration_months} Monate` : getIntervallText(data.abteilung.intervall)}</p>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-xs sm:text-sm text-gray-600">Letzte Zahlung</p>
                <p className="text-sm sm:text-base font-semibold text-gray-900">
                  {data.payment_status.letzte_zahlung ? formatDate(data.payment_status.letzte_zahlung) : "Noch keine Zahlung"}
                </p>
              </div>
              <div>
                <p className="text-xs sm:text-sm text-gray-600">Nächste Fälligkeit</p>
                <p className="text-sm sm:text-base font-semibold text-gray-900">{formatDate(data.payment_status.naechste_zahlung_faellig)}</p>
                {data.payment_status.tage_bis_faellig < 0 ? (
                  <p className="text-xs sm:text-sm text-red-600">{Math.abs(data.payment_status.tage_bis_faellig)} Tage überfällig</p>
                ) : (
                  <p className="text-xs sm:text-sm text-gray-600">in {data.payment_status.tage_bis_faellig} Tagen</p>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payment Options */}
      {data.payment_status.status !== "paid" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Bank Details */}
          {data.bank_details && (
            <Card className="card-white card-hover">
              <CardHeader>
                <CardTitle className="text-base sm:text-lg text-gray-900 flex items-center gap-2">
                  <Building2 className="w-4 h-4 sm:w-5 sm:h-5" />
                  Bankverbindung
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {data.bank_details.bank_name && (
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Bank</p>
                    <p className="text-sm sm:text-base font-semibold text-gray-900">{data.bank_details.bank_name}</p>
                  </div>
                )}
                {data.bank_details.bank_holder && (
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Kontoinhaber</p>
                    <p className="text-sm sm:text-base font-semibold text-gray-900">{data.bank_details.bank_holder}</p>
                  </div>
                )}
                {data.bank_details.iban && (
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">IBAN</p>
                    <p className="font-mono text-xs sm:text-sm text-gray-900 break-all">{data.bank_details.iban}</p>
                  </div>
                )}
                {data.bank_details.bic && (
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">BIC</p>
                    <p className="font-mono text-xs sm:text-sm text-gray-900">{data.bank_details.bic}</p>
                  </div>
                )}
                <div className="pt-2 border-t border-gray-200">
                  <p className="text-xs sm:text-sm text-gray-600">Betrag</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">{formatEuro(paymentAmountCents(contract, data.abteilung))}</p>
                </div>
                {data.bank_details.verwendungszweck && (
                  <div className="pt-2 border-t border-gray-200">
                    <p className="text-xs sm:text-sm text-gray-600">Verwendungszweck</p>
                    <p className="text-xs sm:text-sm font-medium text-gray-900 bg-gray-50 p-2 rounded border border-gray-200 break-words">
                      {data.bank_details.verwendungszweck}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* PayPal */}
          {data.paypal_link && (
            <Card className="card-white card-hover">
              <CardHeader>
                <CardTitle className="text-base sm:text-lg text-gray-900 flex items-center gap-2">
                  <CreditCard className="w-4 h-4 sm:w-5 sm:h-5" />
                  PayPal
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm sm:text-base text-gray-700">Zahlen Sie bequem mit PayPal</p>
                <div>
                  <p className="text-xs sm:text-sm text-gray-600 mb-1">Betrag</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">{formatEuro(paymentAmountCents(contract, data.abteilung))}</p>
                </div>
                <a
                  href={data.paypal_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                  data-testid="paypal-pay-button"
                >
                  <Button className="w-full min-h-[44px]" style={{ background: '#0070BA', color: 'white' }}>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Mit PayPal bezahlen
                  </Button>
                </a>
                <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                  <p className="text-sm text-blue-800 flex items-start gap-2">
                    <span className="text-blue-500 mt-0.5">💡</span>
                    <span>
                      Bitte als <strong>&quot;Freunde &amp; Familie&quot;</strong> senden, damit keine Gebühren anfallen.
                    </span>
                  </p>
                </div>
                <p className="text-xs sm:text-sm text-gray-600 italic">
                  Hinweis: Die Zahlung wird nach Eingang manuell bestätigt.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Payment History */}
      <Card className="card-white">
        <CardHeader>
          <CardTitle className="text-base sm:text-lg text-gray-900">Zahlungshistorie</CardTitle>
        </CardHeader>
        <CardContent>
          {(toArray(data.zahlungen).length === 0) ? (
            <div className="text-center py-8 text-sm sm:text-base text-gray-600">Noch keine Zahlungen vorhanden</div>
          ) : (
            <div className="space-y-3">
              {toArray(data.zahlungen).map((zahlung) => isCreditEntry(zahlung) ? (
                <div key={zahlung.id} className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3 transition-colors sm:p-4" data-testid={`credit-history-${zahlung.id}`}>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100 sm:h-10 sm:w-10"><Gift className="h-4 w-4 text-emerald-700 sm:h-5 sm:w-5" /></div>
                      <div><p className="text-sm font-semibold text-gray-900 sm:text-base">{billingEntryTitle(zahlung)}</p><p className="text-xs text-gray-600 sm:text-sm">{creditReasonLabel(zahlung.reason)} · {creditStatusLabel(zahlung.status)}</p></div>
                    </div>
                    <div className="pl-11 text-left sm:pl-0 sm:text-right">
                      {zahlung.status === "applied" && <><p className="text-xs text-gray-600 sm:text-sm">Nächste Fälligkeit</p><p className="text-xs font-semibold text-gray-900 sm:text-sm">{formatDate(zahlung.new_next_payment_due)}</p></>}
                      {zahlung.status === "pending" && <p className="text-xs font-medium text-amber-800 sm:text-sm">Nach erster Zahlung wirksam</p>}
                    </div>
                  </div>
                  {zahlung.status === "applied" && <p className="mt-3 border-t border-emerald-200 pt-3 text-xs text-gray-600 sm:text-sm">Gratis-Zeitraum: {formatDate(zahlung.period_start)} bis {formatDate(zahlung.period_end)}</p>}
                  {zahlung.status === "voided" && <p className="mt-3 border-t border-gray-200 pt-3 text-xs text-gray-600 sm:text-sm">Diese Gutschrift wurde storniert{zahlung.void_reason ? `: ${zahlung.void_reason}` : "."}</p>}
                  {zahlung.note && <p className="mt-2 text-xs text-gray-600 sm:text-sm">Notiz: {zahlung.note}</p>}
                </div>
              ) : (
                <div
                  key={zahlung.id}
                  className="p-3 sm:p-4 rounded-lg bg-gray-50 border border-gray-200 hover:border-gray-300 transition-colors"
                  data-testid={`payment-history-${zahlung.id}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm sm:text-base font-semibold text-gray-900">{(zahlung.betrag || 0).toFixed(2)} €</p>
                        <p className="text-xs sm:text-sm text-gray-600 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Bezahlt am {formatDate(zahlung.bezahlt_am)}
                        </p>
                        {paymentPeriodLabel(zahlung) && <p className="mt-1 text-xs text-gray-500">Nutzungszeitraum: {paymentPeriodLabel(zahlung)}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3 pl-11 sm:pl-0">
                      <div className="text-left sm:text-right flex-1">
                        <p className="text-xs sm:text-sm text-gray-600">Nächste Fälligkeit</p>
                        <p className="text-xs sm:text-sm font-semibold text-gray-900">{formatDate(zahlung.naechste_zahlung_faellig_am)}</p>
                      </div>
                      {zahlung.rechnung_pdf && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => handleDownloadRechnung(zahlung.id, e)}
                          className="border-blue-300 text-blue-600 hover:bg-blue-50 flex-shrink-0"
                          title="Rechnung herunterladen"
                        >
                          <FileText className="w-4 h-4 sm:mr-2" />
                          <span className="hidden sm:inline">Rechnung</span>
                        </Button>
                      )}
                    </div>
                  </div>
                  {zahlung.notiz && (
                    <div className="mt-3 pt-3 border-t border-gray-300">
                      <p className="text-xs sm:text-sm text-gray-600">Notiz: {zahlung.notiz}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const ContractValue = ({ label, value }) => (
  <div className="rounded-xl bg-gray-50 p-3">
    <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">{label}</p>
    <p className="mt-1 text-sm font-semibold text-gray-900">{value || "–"}</p>
  </div>
);

export default UserDashboard;
